import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './index.css';
import data_en from './table/data_en';
import data_cn from './table/data_cn';
import data_vi from './table/data_vi';
import SEO from '../seo';
import { PAGE_SEO, COMMON_KEYWORDS } from '../seo/pageSeo';

// Check icon SVG
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// X icon SVG
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// Lightning bolt icon SVG
const BoltIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

function AdvantageRow({ item, index, inView }) {
  const { t } = useTranslation();

  return (
    <div className={`advantage-row ${inView ? 'in-view' : ''}`}>
      {/* Our Product */}
      <div className="advantage-card advantage-card--ours">
        <div className="advantage-card__header">
          <div className="advantage-card__icon">
            <BoltIcon />
          </div>
          <span className="advantage-card__label">
            {t('adv_our_product')}
          </span>
        </div>
        <h3 className="advantage-card__title">{item.projectName.text}</h3>
        <p className="advantage-card__body">{item.product.text}</p>
        <div className="advantage-card__status">
          <CheckIcon />
        </div>
      </div>

      {/* Competitor */}
      <div className="advantage-card advantage-card--others">
        <div className="advantage-card__header">
          <div className="advantage-card__icon">
            <XIcon />
          </div>
          <span className="advantage-card__label">
            {t('adv_other_product')}
          </span>
        </div>
        <h3 className="advantage-card__title">{item.projectName.text}</h3>
        <p className="advantage-card__body">{item.other.text}</p>
        <div className="advantage-card__status">
          <XIcon />
        </div>
      </div>
    </div>
  );
}

export default function Advantage() {
  const { t, i18n } = useTranslation();
  const [dataSource, setDataSource] = useState([]);
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const loadDataSource = () => {
      if (i18n.language.startsWith('zh')) {
        setDataSource(data_cn);
      } else if (i18n.language.startsWith('vi')) {
        setDataSource(data_vi);
      } else {
        setDataSource(data_en);
      }
    };
    loadDataSource();
    i18n.on('languageChanged', loadDataSource);

    // Intersection observer for scroll animation
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      i18n.off('languageChanged', loadDataSource);
      observer.disconnect();
    };
  }, [i18n]);

  return (
    <>
    <SEO
        pageKey="advantage"
        title={PAGE_SEO.advantage.title}
        description={PAGE_SEO.advantage.description}
        keywords={COMMON_KEYWORDS}
        slug={PAGE_SEO.advantage.slug}
    />
    <div className="advantage-page">
      {/* Page Header */}
      <header className="advantage-page-header">
        <p className="advantage-eyebrow">
          {t('adv_core_competitiveness')}
        </p>
        <h1 className="advantage-page-title">
          {t('adv_why_choose')}
        </h1>
        <p className="advantage-page-subtitle">
          {t('adv_subtitle')}
        </p>
      </header>

      {/* Comparison Section */}
      <section className="advantage-section" ref={sectionRef}>
        {dataSource.map((item, index) => (
          <AdvantageRow
            key={item.key}
            item={item}
            index={index}
            inView={inView}
          />
        ))}
      </section>

      {/* Bottom CTA */}
      <div className="advantage-cta">
        <div className="advantage-cta__inner">
          <p className="advantage-cta__text">
            {t('adv_cta_line1')}
            <br />
            <span className="advantage-cta__accent">
              {t('adv_cta_accent')}
            </span>
          </p>
          <p className="advantage-cta__sub">
            {t('adv_cta_sub')}
          </p>
        </div>
      </div>
    </div>
    </>
  );
}
